import { getRouteApi } from '@tanstack/react-router';
import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { AGGREGATE_NAMES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { RxHeart, RxHeartFilled } from 'react-icons/rx';

import { useFavoritismOffers } from '../../../hooks/useFavoritismOffers';
import TreatButton from '~/components/TreatButton';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { cn } from '~/utils/cnTailwind';

type Props = {
    acceptedOffer: SchemaLoanRequestResolver_GetAcceptedOffers[number];
    withLabel?: boolean;
};

export const Favorite = ({ acceptedOffer, withLabel = false }: Props) => {
    const { t } = useTranslation();
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const isDisabled = !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].changeOfferFavorability;
    const { favored, id, loanRequestId: aggregateId } = acceptedOffer;
    const favoritismOffer = useFavoritismOffers(
        // NOTE we need always the inverted value of favored to set the new value
        { aggregateId, favored: !favored, id },
    );

    const content = (
        <TreatButton
            data-testid="favoritism-offer"
            onClick={(event) => {
                event.stopPropagation();
                favoritismOffer.mutate();
            }}
            type="button"
            className={cn(
                'flex items-center gap-2 py-1.5 px-3',
                (isDisabled || favoritismOffer.isPending) && 'opacity-50 cursor-not-allowed',
            )}
            disabled={isDisabled || favoritismOffer.isPending}
        >
            {favored ? <RxHeartFilled size={16} color="0098CC" /> : <RxHeart size={16} />}
            {withLabel && <span className="text-xs px-3">{t('OFFER_MANAGEMENT.ICONS_TOOLTIP.FAVORITE')}</span>}
        </TreatButton>
    );

    if (withLabel) {
        return content;
    }

    return (
        <Tooltip>
            <TooltipTrigger>{content}</TooltipTrigger>
            <TooltipContent>{t('OFFER_MANAGEMENT.ICONS_TOOLTIP.FAVORITE')}</TooltipContent>
        </Tooltip>
    );
};
